import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { InputComponent } from '@shared/components/input/input.component';
import { ModalComponent } from '@shared/components/modals/modal/modal.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import {
  BudgetSnapshotType,
  BudgetType,
  EntityType,
  EventType,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventTrackerService } from '@shared/store/event/event-tracker.service';
import { EventService } from '@shared/store/event/event.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InputComponent, ReactiveFormsModule, ModalComponent],
  selector: 'aux-snapshot-modal',
  template: `
    <form [formGroup]="snapshotForm" (ngSubmit)="!ref.data?.name ? onSave() : onUpdate()">
      <aux-modal
        primaryButtonType="submit"
        [loading]="submitting()"
        [primaryButtonDisabled]="submitting()"
      >
        <div class="w-[380px] font-inter">
          <aux-input formControlName="snapshotName" placeholder="Name" validators="required" />
        </div>
      </aux-modal>
    </form>
  `,
})
export class SnapshotModalComponent implements OnInit {
  public ref = inject(CustomOverlayRef<unknown, { name?: string }>);

  private eventService = inject(EventTrackerService);
  private fb = inject(UntypedFormBuilder);
  private globalEventService = inject(EventService);
  private overlayService = inject(OverlayService);

  submitting = signal(false);

  snapshotForm = this.fb.group({
    snapshotName: ['', [Validators.required]],
  });

  ngOnInit(): void {
    if (this.ref.data?.name) {
      this.snapshotForm.setValue({
        snapshotName: this.ref.data.name,
      });
    }
  }

  onSave = async () => {
    if (this.snapshotForm.valid) {
      this.submitting.set(true);

      const { data, errors, success } = await firstValueFrom(
        this.globalEventService.processEvent$({
          entity_id: '',
          entity_type: EntityType.TRIAL,
          payload: JSON.stringify({
            budget_type: BudgetType.BUDGET_SNAPSHOT,
            merge_type: 'MERGE_BUDGET_SNAPSHOT',
            snapshot_type: BudgetSnapshotType.BUDGET_SNAPSHOT_USER_CREATED,
            user_description: this.snapshotForm.value.snapshotName,
          }),
          type: EventType.CREATE_TRIAL_BUDGET_SNAPSHOT,
        })
      );

      if (data) {
        void this.eventService.trackEvent(data.id);
      }

      if (success) {
        this.ref.close();
      } else {
        this.overlayService.error(errors);
      }

      this.submitting.set(false);
    }
  };

  onUpdate = () => {
    this.ref.close({ name: this.snapshotForm.value.snapshotName });
  };
}
